import './Login.scss';

import { Input } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { graphql, useStaticQuery } from 'gatsby';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LocalizedLink } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import accountIcon from '../../images/englander-icons/Account-Icon.svg';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useSessionStorage } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/useLocalStorage';
import { useSignInAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/customerAccount';
import { useTranslation } from 'react-i18next';
import { validateEmail } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/validations';
import EnglanderButton from '../Primitives/EnglanderButton';

interface ILoginProps {
    action?: () => void;
    className?: string;
}

export default function Login(props: ILoginProps) {
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({
        email: '',
        password: '',
    });
    const { addAlert } = useContext(feedbackContext);

    const { t } = useTranslation('layout');
    const [signIn] = useSignInAction();
    const [redirectUriStorage, setRedirectUriStorage] =
        useSessionStorage('redirectUri');
    const [redirectUriQueryArg, setRedirectUriQueryArg] =
        useQueryArg('redirect_uri');

    useEffect(() => {
        if (redirectUriQueryArg) {
            setRedirectUriStorage(
                redirectUriQueryArg,
                30 * 60 /* 30 minutes */
            );
            setRedirectUriQueryArg('');
        }
    }, [redirectUriQueryArg, setRedirectUriQueryArg, setRedirectUriStorage]);

    const isFormValid = useCallback(() => {
        let isValid = true;

        for (const value of Object.values(errors)) {
            if (value.trim() !== '') {
                isValid = false;
                break;
            }
        }

        if (!email) {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                email: t('error.invalid-email'),
            }));
        }

        if (!password) {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                password: t('error.field-required'),
            }));
        }

        return isValid;
    }, [email, errors, password, t]);

    const onLoginButtonClicked = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isFormValid()) {
                signIn(email, password)
                    .then(() => {
                        props?.action && props.action();
                        navigateTranslated('/account/my-account/');
                        setRedirectUriStorage(undefined);
                    })
                    .catch((error) => {
                        console.log(error);
                        setErrors((errors) => ({
                            ...errors,
                            email: ' ',
                            password: t('error.invalid-login'),
                        }));
                    });
            }

            return false;
        },
        [
            isFormValid,
            signIn,
            email,
            password,
            redirectUriStorage,
            t,
            setRedirectUriStorage,
            props,
        ]
    );

    const { loginMessage } = useStaticQuery(graphql`
        query {
            loginMessage: allProductsJson(
                filter: { code: { eq: "englander_connexion" } }
            ) {
                nodes {
                    description {
                        en_CA
                        en_US
                        fr_CA
                    }
                    attributes {
                        texte_3 {
                            values {
                                en_CA
                                en_US
                                fr_CA
                            }
                        }
                    }
                }
            }
        }
    `);

    return (
        <div className={`login ${props.className}`}>
            <FontAwesomeIcon
                className='close'
                icon={faTimes}
                onClick={() => {
                    document.body.style.overflow = 'scroll';
                    props?.action && props.action();
                }}
            />
            <h2>{t('login.title')}</h2>
            <p className='login-message'>
                {loginMessage?.nodes?.[0]?.description?.[currentLocale]}
            </p>

            <form
                className='login-form'
                onSubmit={(event) => onLoginButtonClicked(event)}
            >
                <div className='login-inputs'>
                    <div>
                        <Input
                            id='email'
                            type='text'
                            className={errors.email === '' ? `ok` : `error`}
                            value={email}
                            onChange={(value) => setEmail(value)}
                            placeholder={t('login.email')}
                            autocomplete='username'
                            validateInput={() => validateInput(email, 'email')}
                        />
                        <span className='error-msg'>{errors?.email}</span>
                    </div>
                    <div>
                        <Input
                            id='password'
                            type='password'
                            className={errors.password === '' ? `ok` : `error`}
                            value={password}
                            onChange={(value) => setPassword(value)}
                            placeholder={t('login.password')}
                            autocomplete='current-password'
                            validateInput={() =>
                                validateInput(password, 'password')
                            }
                        />
                        <span className='error-msg'>{errors?.password}</span>
                    </div>
                </div>

                <div className='login-btns'>
                    <div className='forgot-password'>
                        <LocalizedLink
                            // L'Animation pour fermer le popup est perdu à cause de la navigation
                            label={t('login.forgot')}
                            to='/forgot-password/'
                        />
                        <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                    <EnglanderButton
                        className='login-btn'
                        label={t('login.login')}
                        iconRight={accountIcon}
                    />
                </div>
            </form>
        </div>
    );

    function validateInput(value: string, keyName: string) {
        if (value === '') {
            setErrors((errors: any) => ({
                ...errors,
                [keyName]: t('error.field-required'),
            }));
        } else {
            setErrors((errors: any) => ({
                ...errors,
                [keyName]: '',
            }));
        }

        if (keyName === 'email') {
            if (value !== '' && !validateEmail(value)) {
                setErrors((errors: any) => ({
                    ...errors,
                    [keyName]: t('error.invalid-email'),
                }));
            }
        }
    }
}
